import React, { useState, useEffect } from "react";

const SERVICES = [
  "EdTech Solutions",
  "Web Development",
  "Mobile App Development",
  "E-Commerce Solutions",
  "Custom Software Development",
  "Corporate Training",
  "Workshops",
  "Website Maintenance",
  "Digital Solutions",
  "AI Solutions",
  "UI / UX Design",
  "Cloud & DevOps Services",
  "Search Engine Optimization (SEO)",
  "Social Media Management",
  "API Development & Integration",
  "Analytics & BI Solutions",
  "Cybersecurity Services",
  "CRM & ERP Solutions",
  "Quality Assurance & Testing",
  "IT Consulting Services",
];

const formatHour = (h) => {
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  const period = h < 12 ? "AM" : "PM";
  return `${hour12} ${period}`;
};

const generateTimeSlots = (startHour, endHour) => {
  const slots = [];
  for (let h = startHour; h < endHour; h++) {
    slots.push(`${formatHour(h)} - ${formatHour(h + 1)}`);
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots(9, 19);

const FreeCounselingPage = ({ onClose }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    status: "",
    service: "",
    experience: "",
    counselingDate: "",
    timeSlot: "",
    problem: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: false }));
    setStatusMsg("");
  };

  const validate = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/;

    if (!formData.name.trim()) newErrors.name = true;
    if (!emailRegex.test(formData.email)) newErrors.email = true;
    if (!phoneRegex.test(formData.phone)) newErrors.phone = true;
    if (!formData.status) newErrors.status = true;
    if (!formData.counselingDate) newErrors.counselingDate = true;
    if (!formData.experience.trim()) newErrors.experience = true;
    if (!formData.timeSlot) newErrors.timeSlot = true;
    if (!formData.problem.trim()) newErrors.problem = true;
    if (!formData.service) newErrors.service = true;

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setStatusMsg("Please fill all required fields");
      return;
    }

    try {
      setLoading(true);

      const response = await fetch("/api/counseling", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.status === "success") {
        setStatusMsg("We've received your request — our team will reach out shortly!");
        setFormData({
          name: "",
          email: "",
          phone: "",
          status: "",
          service: "",
          experience: "",
          counselingDate: "",
          timeSlot: "",
          problem: "",
        });
        setErrors({});
      } else {
        setStatusMsg(data.message || "Failed to submit request");
      }
    } catch (err) {
      setStatusMsg(err.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const styles = getStyles(isMobile);

  const inputStyle = (field) => ({
    ...styles.input,
    borderBottom: errors[field] ? "2px solid #dc2626" : "1.5px solid #6C0A9F",
    backgroundColor: errors[field] ? "rgba(220, 38, 38, 0.05)" : "transparent",
  });

  const textareaStyle = (field) => ({
    ...styles.textarea,
    borderBottom: errors[field] ? "2px solid #dc2626" : "1.5px solid #6C0A9F",
    backgroundColor: errors[field] ? "rgba(220, 38, 38, 0.05)" : "transparent",
  });

  const selectStyle = (field) => ({
    ...styles.input,
    borderBottom: errors[field] ? "2px solid #dc2626" : "1.5px solid #6C0A9F",
    backgroundColor: errors[field] ? "rgba(220, 38, 38, 0.05)" : "transparent",
  });

  return (
    <>
      <section style={styles.section}>
        {/* CLOSE BUTTON */}
        {onClose && (
          <button onClick={onClose} style={styles.closeBtn} aria-label="Close modal">
            ×
          </button>
        )}

        <div style={styles.contentWrapper}>
          <span style={styles.eyebrow}>Let's Build Solutions Together</span>
          <h3 style={styles.heading}>
            Have a project in mind?{" "}
            <span style={styles.highlight}>Let's discuss it — free</span>
          </h3>

          <form style={styles.form} onSubmit={handleSubmit}>
            {/* LEFT COLUMN */}
            <div style={styles.column}>
              <div style={styles.inputGroup}>
                <input
                  name="name"
                  placeholder="Full Name *"
                  value={formData.name}
                  onChange={handleChange}
                  style={inputStyle("name")}
                />
              </div>

              <div style={styles.inputGroup}>
                <input
                  name="email"
                  placeholder="Email *"
                  value={formData.email}
                  onChange={handleChange}
                  style={inputStyle("email")}
                />
              </div>

              <div style={styles.inputGroup}>
                <input
                  name="phone"
                  placeholder="Mobile *"
                  maxLength={10}
                  value={formData.phone}
                  onChange={handleChange}
                  style={inputStyle("phone")}
                />
              </div>

              <div style={styles.inputGroup}>
                <textarea
                  name="problem"
                  placeholder="Tell us about your project or requirement *"
                  value={formData.problem}
                  onChange={handleChange}
                  style={textareaStyle("problem")}
                />
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div style={styles.column}>
              <div style={styles.inputGroup}>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  style={selectStyle("status")}
                >
                  <option value="" style={styles.option}>You Are A *</option>
                  <option value="Individuals" style={styles.option}>Individuals</option>
                  <option value="Business Owner" style={styles.option}>Business Owner</option>
                </select>
              </div>

              {/* SERVICES DROPDOWN */}
              <div style={styles.inputGroup}>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  style={selectStyle("service")}
                >
                  <option value="" style={styles.option}>Service You're Interested In *</option>
                  {SERVICES.map((service, i) => (
                    <option key={i} value={service} style={styles.option}>
                      {service}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.inputGroup}>
                <input
                  name="experience"
                  placeholder="Team / Experience (years) *"
                  value={formData.experience}
                  onChange={handleChange}
                  style={inputStyle("experience")}
                />
              </div>

              <div style={styles.inputGroup}>
                <select
                  name="timeSlot"
                  value={formData.timeSlot}
                  onChange={handleChange}
                  style={selectStyle("timeSlot")}
                >
                  <option value="" style={styles.option}>Preferred Time Slot *</option>
                  {TIME_SLOTS.map((slot, i) => (
                    <option key={i} value={slot} style={styles.option}>{slot}</option>
                  ))}
                </select>
              </div>

              <div style={styles.inputGroup}>
                <input
                  type="date"
                  name="counselingDate"
                  value={formData.counselingDate}
                  onChange={handleChange}
                  style={inputStyle("counselingDate")}
                />
              </div>
            </div>

            {/* BUTTON / STATUS */}
            <div style={styles.fullWidth}>
              {statusMsg && (
                <p
                  style={{
                    ...styles.status,
                    color: Object.keys(errors).length ? "#dc2626" : "#16a34a",
                  }}
                >
                  {statusMsg}
                </p>
              )}
              <button disabled={loading} style={styles.button} className="fc-submit-btn">
                {loading ? "Sending..." : "Discuss Your Project"}
              </button>
            </div>
          </form>
        </div>
      </section>

      <style>{`
        .fc-submit-btn {
          background-color: #6C0A9F !important;
          color: #ffffff !important;
          box-shadow: 0 4px 14px rgba(108, 10, 159, 0.25);
        }
        .fc-submit-btn:hover:not(:disabled) {
          background-color: #55077d !important;
          transform: translateY(-2px);
          box-shadow: 0 8px 22px rgba(108, 10, 159, 0.4);
        }
        section input::placeholder,
        section textarea::placeholder {
          color: #94a3b8;
        }
        section input, section select, section textarea {
          color: #0f172a;
          box-sizing: border-box;
          width: 100%;
        }
        section input:focus, section select:focus, section textarea:focus {
          outline: none;
          border-bottom: 2px solid #55077d !important;
        }
      `}</style>
    </>
  );
};

/* ================= STYLES ================= */
const getStyles = (isMobile) => ({
  section: {
    padding: isMobile ? "24px 16px" : "36px 40px",
    background: "transparent",
    position: "relative",
    width: "100%",
    boxSizing: "border-box",
  },
  closeBtn: {
    position: "absolute",
    top: "14px",
    right: "18px",
    background: "none",
    border: "none",
    fontSize: "26px",
    fontWeight: "bold",
    cursor: "pointer",
    color: "#6C0A9F",
    zIndex: 10,
    lineHeight: 1,
  },
  contentWrapper: {
    maxWidth: "920px",
    margin: "0 auto",
  },
  eyebrow: {
    display: "block",
    color: "#6C0A9F",
    fontSize: "13px",
    fontWeight: 700,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    textAlign: "center",
    marginBottom: "8px",
  },
  heading: {
    textAlign: "center",
    marginBottom: "28px",
    color: "#0f172a",
    fontSize: isMobile ? "20px" : "24px",
    fontWeight: 700,
    lineHeight: 1.35,
  },
  highlight: {
    color: "#6C0A9F",
  },
  form: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
    columnGap: "32px",
    rowGap: "16px",
    alignItems: "start",
  },
  column: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  input: {
    padding: "10px 4px",
    borderRadius: "0",
    border: "none",
    fontSize: "14.5px",
    transition: "border-color 0.2s ease, background-color 0.2s ease",
  },
  textarea: {
    padding: "10px 4px",
    borderRadius: "0",
    border: "none",
    minHeight: "100px",
    fontSize: "14.5px",
    resize: "vertical",
    transition: "border-color 0.2s ease, background-color 0.2s ease",
  },
  option: {
    background: "#ffffff",
    color: "#0f172a",
  },
  fullWidth: {
    gridColumn: "1 / -1",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginTop: "16px",
  },
  button: {
    width: "100%",
    maxWidth: "320px",
    padding: "14px 20px",
    borderRadius: "10px",
    border: "none",
    fontWeight: "700",
    fontSize: "15px",
    cursor: "pointer",
    transition: "all 0.25s ease",
  },
  status: {
    marginBottom: "10px",
    fontSize: "14px",
    fontWeight: 600,
    textAlign: "center",
  },
});

export default FreeCounselingPage;